// Tiny unobtrusive corner button to enter or exit the authoring view.
// Clicking it toggles `?dev=1` in the URL and reloads — the page reloads
// (rather than mounting/unmounting the dev panel live) because the dev
// UI lifecycle is wired at boot in app.ts. Cheap, predictable, and the
// asset cache means it's near-instant on the second load.
//
// DM12 (DECIDED, John 2026-06-23): a tiny frosted-glass circle that grows on
// hover — stripped + minimal in the demo/performance view, so a festival
// visitor sees a clean dot, not a chrome button. ⚠ Frosted glass uses
// backdrop-filter, the one compositor-flash suspect from the flicker saga
// ([[lec_flicker_cause]] mechanism #1). A small STATIC circle is low-risk, but
// John should verify it on the Thunderbolt screen; if it flickers, drop the
// `backdrop-filter` line below (the rgba background alone is the no-blur frost).
export function createDevToggle(currentlyDev: boolean): void {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.title = currentlyDev ? 'Exit dev view' : 'Open dev panel';
  const REST = 13; // px — a quiet frosted dot at rest
  const GROWN = 34; // px — grows to a real touch target + reveals the glyph on hover
  btn.style.cssText = [
    'position: fixed',
    'top: 16px',
    'right: 16px',
    `width: ${REST}px`,
    `height: ${REST}px`,
    'padding: 0',
    'background: rgba(15, 15, 15, 0.4)',
    'color: #ddd',
    'border: 1px solid rgba(255, 255, 255, 0.18)',
    'border-radius: 999px',
    'cursor: pointer',
    'font: 14px/1 system-ui, sans-serif',
    'backdrop-filter: blur(6px)', // ⚠ flicker suspect — see header note; safe to drop
    'z-index: 1000',
    'user-select: none',
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'overflow: hidden',
    'opacity: 0.6',
    'touch-action: manipulation',   // no 300ms iOS tap delay
    // grow + reveal the glyph on hover; anchored top-right so it expands down-left
    'transition: width 140ms ease, height 140ms ease, opacity 140ms ease',
  ].join('; ');

  // The glyph is hidden at rest (the dot is "stripped") and fades in as the
  // circle grows on hover — so the performance view stays clean.
  const glyph = document.createElement('span');
  glyph.textContent = currentlyDev ? '×' : '⚙';
  glyph.style.cssText = 'opacity: 0; transition: opacity 140ms ease; line-height: 1;';
  btn.appendChild(glyph);

  const grow = (on: boolean): void => {
    btn.style.width = btn.style.height = `${on ? GROWN : REST}px`;
    btn.style.opacity = on ? '1' : '0.6';
    glyph.style.opacity = on ? '1' : '0';
  };
  // Pointer events fire for both mouse hover AND touch contact — so the
  // visual emphasis works for both input modes.
  btn.addEventListener('pointerenter', () => grow(true));
  btn.addEventListener('pointerleave', () => grow(false));
  btn.addEventListener('focus', () => grow(true));
  btn.addEventListener('blur', () => grow(false));

  btn.addEventListener('click', () => {
    const url = new URL(window.location.href);
    if (currentlyDev) url.searchParams.delete('dev');
    else url.searchParams.set('dev', '1');
    window.location.href = url.toString();
  });

  document.body.appendChild(btn);
}
